/**
 * The three live numbers the navigation wears, and what each one costs.
 *
 * WHERE EACH NUMBER COMES FROM.
 *
 *   - Work queue and Approvals read `total` off the dashboard's own list hooks,
 *     at their own default page sizes. Called rather than copied, so on the
 *     dashboard the sidebar and the page share one cache entry each and those
 *     two badges cost nothing. Asking for a `limit=1` page instead would split
 *     each entry in two and make the dashboard pay double, for no gain: `total`
 *     is the whole queue in both routes whatever the page size.
 *   - Inbox reads `unread` off `GET /inbox/counts`, through the inbox page's
 *     own hook and so its own cache key. On the inbox page that badge is free
 *     for the same reason, and every mark-read or mark-unread the page makes
 *     already invalidates that key, so the badge moves the moment a thread is
 *     read. Elsewhere it is one grouped count read, refetched each minute by the
 *     hook, rather than a list request.
 *
 * All three are asked for once per app load rather than once per page. The
 * shell never unmounts, so moving between screens re-renders the sidebar and
 * issues nothing new.
 *
 * WHAT THE INBOX NUMBER DOES AND DOES NOT AGREE WITH. It is the same figure the
 * Unread tab prints, for the active brand. It is not the size of whatever list
 * the Inbox link opens, which lands on All, and it is not a cross-brand figure
 * when an admin is looking at every brand at once, because the counts endpoint
 * has no all-brands form. Queue and Approvals are the totals printed beside
 * those lists on the dashboard. So a badge agrees with a named figure on a
 * screen, and not necessarily with the length of the list the link opens.
 *
 * WHAT IT DOES NOT DO. It is not gated, except the Prospecting three, which
 * are asked only for somebody who sees those rows. The shell only renders behind
 * `RequireAuth`, so the session has landed by the time this runs, and the one
 * state left over is a person who belongs to no brand yet: they get refusals,
 * which are not retried, which nothing renders. Gating would mean rebuilding
 * these cache keys by hand, and the keys matching the screens' exactly is the
 * reason the badges are free where it matters.
 */
import { useOpenCards, usePendingApprovals } from '../pages/dashboard/ui/data'
import { useInboxCounts } from '../pages/inbox/api'
import { useSession } from '../lib/session'
import { useReviewCount } from '../pages/prospecting/review/useReviewCount'
import { brandsForWorkspace, workspaceBrandId } from '../pages/prospecting/shared/brand-model'
import { useProspectingBrands } from '../pages/prospecting/shared/useProspectingBrand'
import { useProspectingSeat } from '../pages/prospecting/shared/useProspectingSeat'
import { navCountsFrom, type NavCounts } from './sidebar-model'

export function useNavCounts(): NavCounts {
  const cards = useOpenCards()
  const approvals = usePendingApprovals()
  const inbox = useInboxCounts(true)

  /*
   * `undefined` rather than 0 while a request is in flight or refused. The two
   * are different facts and the badge draws neither, but only one of them is a
   * promise: reporting 0 for "not asked yet" would let a later caller write
   * "nothing waiting" over a queue nobody has looked at.
   */
  return { ...navCountsFrom({ cards: cards.data, approvals: approvals.data, inbox: inbox.data }), ...useProspectingCounts() }
}

/**
 * The Prospecting rows' three counts, through the gateway (/api/v1/prospecting).
 *
 * The old app's own column wore them, and since 2026-10-21 this column has
 * worn them in both places. Scoped to the workspace: the service brands linked
 * to the active brand, or every brand when none is chosen, none is linked, or
 * the every-brand view is on. Asked only for somebody who sees the rows.
 *
 * Two reads, both shared with the pages rather than asked twice: the brand
 * list (`useProspectingBrands`, the filter's own query, read each minute) for
 * posts waiting and prospects ready, and the Queue's count (`useReviewCount`,
 * the key the Queue writes to when it approves something) for what waits for
 * a yes. A service that does not answer draws no badge.
 */
export function useProspectingCounts(): Pick<NavCounts, 'prospectingReview' | 'prospectingWaiting' | 'prospectingProspects'> {
  const { activeBrand, superAdminView } = useSession()
  const seat = useProspectingSeat()
  const brands = useProspectingBrands(seat)
  const shown = (brands.data?.brands ?? []).filter((brand) => brand.enabled !== false)
  const keys = brandsForWorkspace(shown, workspaceBrandId({ superAdminView, activeBrandId: activeBrand?.id ?? null }))
  const mine = keys === 'all' ? shown : shown.filter((brand) => keys.includes(brand._id))
  const review = useReviewCount(keys === 'all' ? 'all' : keys.join(','), { enabled: seat && brands.data !== undefined })
  if (brands.data === undefined) return {}
  return {
    prospectingReview: review,
    prospectingWaiting: mine.reduce((n, brand) => n + (brand.waiting ?? 0), 0),
    prospectingProspects: mine.reduce((n, brand) => n + (brand.prospects_ready ?? 0), 0),
  }
}
